import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { invariant } from 'shared/utils/utils';
import { RenderUnsettledUI } from 'shared/components';

import { Header, LwButton, Page, Toolbox } from 'redesign';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { IconApproved, IconCancel } from 'assets/img';
import { PartnerInformationEditForm } from './partner-information-edit-form';
import {
  PartnerInformationEditFormData,
  PartnerInformationFormData,
} from './partner-information.types';
import { routes } from '../../../../../Routes';
import { useGetPartner } from '../../partners/hooks/use-get-partner';
import { partnersAlerts, useAlert } from '../../../../../shared/components/alerts';
import { useEditPartnerInformation } from '../hooks/use-edit-partner-information';

export const PartnerInfoEdit = () => {
  const { id } = useParams<'id'>();
  invariant(id);
  const partnerQuery = useGetPartner(id);
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const { t } = useTranslation(['hq-screen-specific', 'web-common']);
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<PartnerInformationFormData>({});

  const mutation = useEditPartnerInformation(id);

  const handleSubmit = () => {
    formMethods.handleSubmit((data: PartnerInformationFormData) => {
      const submissionData: PartnerInformationEditFormData = {
        ...data,
        deactivated: data.deactivated === 'true',
      };
      mutation.mutate(submissionData, {
        onSuccess: () => {
          alertSuccess(partnersAlerts.success.updated);
          navigate(generatePath(routes.PartnerDetail, { id }));
        },
        onError: (e) => alertError(e),
      });
    })();
  };

  if (partnerQuery.status !== 'success') {
    return <RenderUnsettledUI data={partnerQuery} />;
  }

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText={t('partners.info_edit.title', { ns: 'hq-screen-specific' })} />}
        toolbox={
          <Toolbox underline>
            <LwButton
              onClick={handleSubmit}
              iconColorMode="stroke"
              color="primary"
              startIcon={<IconApproved />}
            >
              {t('partners.info_edit.submit', { ns: 'hq-screen-specific' })}
            </LwButton>
            <LwButton
              onClick={() => navigate(generatePath(routes.PartnerDetail, { id }))}
              iconColorMode="fill"
              color="secondary"
              startIcon={<IconCancel />}
            >
              {t('buttons.cancel', { ns: 'web-common' })}
            </LwButton>
          </Toolbox>
        }
      >
        <PartnerInformationEditForm partner={partnerQuery.data} />
      </Page>
    </FormProvider>
  );
};
